import { FileUp, GitPullRequest, Plus } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Alert } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { ConfirmationDialog } from '@/shared/ui/confirmation-dialog';
import { SelectField } from '@/shared/ui/select';
import { LoadingStatus } from '@/shared/ui/spinner';

import { runSuiteLabels } from '../field-object-labels';
import { readFieldSources } from '../field-source.service';
import { fieldErrorMessage } from '../field-view';

import { FieldEmpty, FieldPanelHeading, FieldStatus, FieldTime } from './field-primitives';
import { FieldReviewReportCard } from './field-review-report';
import { FieldSharing } from './field-sharing';
import { FIELD_INPUT, FIELD_LABEL } from './field-types';

import './field-objects.css';

import type { FieldPanelProps } from './field-types';
import type { FieldPrCaptureInput } from '../api/use-field-workspace';
import type { FieldDestination } from '../field-overview';
import type { FieldActorId, FieldCommand, FieldPr } from '@mio/shared';

type SourceFile = Extract<FieldCommand['action'], { type: 'pr.open' }>['files'][number];
interface CaptureProps {
  onCapture?: (input: FieldPrCaptureInput) => Promise<boolean>;
  captureReady?: boolean;
  onOpenEditor?: () => void;
}

const defaultReviewers: FieldActorId[] = ['noor', 'jordan'];
const reviewFocus: Record<FieldActorId, string> = {
  jordan: 'Product experience and agreed requirements',
  noor: 'Code, architecture and test coverage',
  elena: 'Permissions, approvals and security',
  marcus: 'Business outcomes and pilot scope',
  amara: 'Recovery, operations and handoff',
};

function ReviewerPicker({
  snapshot,
  selected,
  onChange,
  disabled,
}: Pick<FieldPanelProps, 'snapshot'> & {
  selected: FieldActorId[];
  onChange: (value: FieldActorId[]) => void;
  disabled: boolean;
}) {
  return (
    <fieldset disabled={disabled} data-field-tour="submission-reviewers">
      <legend className={FIELD_LABEL}>Reviewers</legend>
      <div className="grid gap-2 sm:grid-cols-2">
        {snapshot.actors.map((actor) => (
          <label key={actor.id} className="field-object-choice items-start">
            <input
              type="checkbox"
              checked={selected.includes(actor.id)}
              onChange={(event) =>
                onChange(
                  event.target.checked
                    ? [...selected, actor.id]
                    : selected.filter((id) => id !== actor.id),
                )
              }
            />
            <span>
              <span className="block">{actor.name.split(' ')[0]}</span>
              <span className="text-fg-muted block text-xs">{reviewFocus[actor.id]}</span>
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function NativeCapture({
  snapshot,
  busy,
  onCapture,
  captureReady = true,
  onOpenEditor,
  input,
  onComplete,
}: Pick<FieldPanelProps, 'snapshot' | 'busy'> &
  CaptureProps & {
    input: FieldPrCaptureInput;
    onComplete?: () => void;
  }) {
  const [capturing, setCapturing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  if (!onCapture) return null;
  return (
    <div className="space-y-2" data-field-tour="submission-capture">
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="secondary"
          loading={capturing}
          disabled={
            busy ||
            capturing ||
            !input.title.trim() ||
            input.reviewerIds?.length === 0 ||
            !snapshot.capabilities.workspace ||
            !captureReady
          }
          onClick={async () => {
            setCapturing(true);
            setError(null);
            setSaved(false);
            try {
              if (await onCapture(input)) {
                setSaved(true);
                onComplete?.();
              }
            } catch (cause) {
              setError(fieldErrorMessage(cause));
            } finally {
              setCapturing(false);
            }
          }}
        >
          {input.prId ? 'Submit updated code from Workbench IDE' : 'Submit from Workbench IDE'}
        </Button>
        {onOpenEditor && snapshot.capabilities.workspace ? (
          <Button type="button" variant="ghost" disabled={busy || capturing} onClick={onOpenEditor}>
            Open Workbench editor
          </Button>
        ) : null}
      </div>
      <p className="text-fg-muted text-xs">
        {!snapshot.capabilities.workspace
          ? 'Native Workbench IDE capture is unavailable here. Upload a complete source snapshot below.'
          : !captureReady
            ? 'Open the editor, start the Workbench workspace and connect the IDE before capturing. Your PR draft stays here.'
            : 'Submits a frozen copy of your connected editor files, starts checks, and sends it to your reviewers. Keep the IDE connected until submission finishes.'}
      </p>
      {error ? <Alert>{error}</Alert> : null}
      {saved && input.prId ? (
        <p role="status" className="text-xs">
          Updated code submitted. Your reviewers will send feedback here and in Conversations.
        </p>
      ) : null}
    </div>
  );
}

function SourcePicker({
  onFiles,
  files,
  busy,
}: {
  onFiles: (files: SourceFile[]) => void;
  files: SourceFile[];
  busy: boolean;
}) {
  const [error, setError] = useState<string | null>(null);
  const [reading, setReading] = useState(false);
  const [excluded, setExcluded] = useState<string[]>([]);
  const read = async (files: File[]) => {
    if (busy || reading || !files.length) return;
    setReading(true);
    setError(null);
    setExcluded([]);
    onFiles([]);
    try {
      const sources = await readFieldSources(files, { onExcluded: setExcluded });
      onFiles(sources);
    } catch (cause) {
      setError(fieldErrorMessage(cause));
    } finally {
      setReading(false);
    }
  };
  return (
    <div className="space-y-2" data-field-tour="submission-sources">
      <div className="flex flex-wrap gap-4">
        <label className="field-source-picker">
          <FileUp className="size-4" aria-hidden="true" />
          Choose source files
          <input
            className="sr-only"
            type="file"
            multiple
            disabled={reading || busy}
            onChange={(event) => {
              void read(Array.from(event.target.files ?? []));
              event.target.value = '';
            }}
          />
        </label>
        <label className="field-source-picker">
          Choose project folder
          <input
            ref={(element) => {
              element?.setAttribute('webkitdirectory', '');
            }}
            className="sr-only"
            type="file"
            multiple
            disabled={reading || busy}
            onChange={(event) => {
              void read(Array.from(event.target.files ?? []));
              event.target.value = '';
            }}
          />
        </label>
      </div>
      {reading ? (
        <LoadingStatus label="Reading selected source…" />
      ) : (
        <p className="text-fg-muted text-xs" role="status">
          {files.length
            ? `${files.length} text ${files.length === 1 ? 'file' : 'files'} selected`
            : 'UTF-8 source only · up to 120 files · 200 KB per file · 1.2 MB total'}
        </p>
      )}
      <p className="text-fg-muted text-xs">
        Choose the project root folder to preserve directories. Individual file selection uses
        filenames only. Known secrets, dependencies and generated caches are excluded automatically.
      </p>
      {files.length ? (
        <details className="text-xs">
          <summary className="cursor-pointer">Review included paths ({files.length})</summary>
          <ul className="mt-2 max-h-40 space-y-1 overflow-auto font-mono">
            {files.map((file) => (
              <li key={file.path}>{file.path}</li>
            ))}
          </ul>
        </details>
      ) : null}
      {excluded.length ? (
        <details className="text-fg-muted text-xs">
          <summary className="cursor-pointer">
            Excluded private or generated files ({excluded.length})
          </summary>
          <ul className="mt-2 max-h-40 space-y-1 overflow-auto font-mono">
            {excluded.map((path) => (
              <li key={path}>{path}</li>
            ))}
          </ul>
        </details>
      ) : null}
      {error ? <Alert>{error}</Alert> : null}
    </div>
  );
}

function ReviewRequest({
  pr,
  snapshot,
  busy,
  onAction,
  ...captureProps
}: FieldPanelProps & CaptureProps & { pr: FieldPr }) {
  const [requesting, setRequesting] = useState(false);
  const [sentRevision, setSentRevision] = useState<string | null>(null);
  const [selection, setSelection] = useState<{ revision: string; ids: FieldActorId[] } | null>(
    null,
  );
  const recipients =
    selection?.revision === pr.revision
      ? selection.ids
      : [...new Set([...defaultReviewers, ...(pr.requiredReviewers ?? [])])];
  const [purpose, setPurpose] = useState('');
  const [files, setFiles] = useState<SourceFile[]>([]);
  return (
    <div className="mt-4 space-y-4">
      <section>
        <Button
          size="sm"
          variant="secondary"
          aria-expanded={requesting}
          disabled={!snapshot.capabilities.review}
          onClick={() => setRequesting(!requesting)}
        >
          Request another review
        </Button>
        <form
          hidden={!requesting}
          className="border-line mt-3 space-y-3 border-l-2 pl-4"
          onSubmit={(event) => {
            event.preventDefault();
            void onAction({
              type: 'pr.request_review',
              prId: pr.id,
              revision: pr.revision,
              actorIds: recipients,
              purpose,
            }).then((saved) => {
              if (saved) {
                setSentRevision(pr.revision);
                setRequesting(false);
              }
            });
          }}
        >
          <p className="text-fg-muted text-sm">
            Reviewing revision <code title={pr.revision}>{pr.revision.slice(0, 8)}</code>. Choose
            the people whose feedback you need. Product feedback and technical approval stay
            distinct in the report.
          </p>
          {pr.requiredReviewers ? (
            <p className="text-sm">
              Required for this revision:{' '}
              {pr.requiredReviewers
                .map(
                  (id) =>
                    snapshot.actors.find((actor) => actor.id === id)?.name.split(' ')[0] ?? id,
                )
                .join(', ')}
              .
            </p>
          ) : null}
          <ReviewerPicker
            snapshot={snapshot}
            selected={recipients}
            disabled={busy}
            onChange={(ids) => setSelection({ revision: pr.revision, ids })}
          />
          <label htmlFor={`purpose-${pr.id}`} className={FIELD_LABEL}>
            What should they review?
          </label>
          <textarea
            id={`purpose-${pr.id}`}
            className={FIELD_INPUT}
            rows={2}
            maxLength={4000}
            value={purpose}
            onChange={(event) => setPurpose(event.target.value)}
          />
          <Button
            type="submit"
            size="sm"
            disabled={busy || !recipients.length || !snapshot.capabilities.review}
          >
            Request review from{' '}
            {recipients
              .map(
                (id) => snapshot.actors.find((actor) => actor.id === id)?.name.split(' ')[0] ?? id,
              )
              .join(', ') || 'selected people'}
          </Button>
        </form>
        {sentRevision === pr.revision ? (
          <p role="status" className="mt-2 text-sm">
            Review requested for this revision.
          </p>
        ) : null}
      </section>
      {captureProps.onCapture ? (
        <section
          className="border-line rounded-md border p-3"
          aria-label="Submit an updated version"
        >
          <h4 className="text-sm font-semibold">Submit an updated version</h4>
          <p className="text-fg-muted my-3 text-xs">
            Send your fixes in this same submission. Checks and reviews restart for the updated
            code, with earlier feedback kept for comparison.
          </p>
          <NativeCapture
            {...captureProps}
            snapshot={snapshot}
            busy={busy}
            input={{
              prId: pr.id,
              title: pr.title,
              description: pr.description,
              ticketIds: pr.ticketIds,
              reviewerIds: recipients,
            }}
          />
        </section>
      ) : null}
      <details className="border-line rounded-md border p-3">
        <summary className="cursor-pointer text-sm">Upload a revised source snapshot</summary>
        <div className="mt-3 space-y-3">
          <p className="text-fg-muted text-xs">
            Upload the complete project. Your reviewers receive the new revision automatically.
          </p>
          <SourcePicker onFiles={setFiles} files={files} busy={busy} />
          <Button
            variant="secondary"
            size="sm"
            disabled={busy || !files.length || !recipients.length}
            onClick={() =>
              void onAction({
                type: 'pr.revise',
                prId: pr.id,
                files,
                reviewerIds: recipients,
              }).then((sent) => {
                if (sent) setFiles([]);
              })
            }
          >
            Submit updated source
          </Button>
        </div>
      </details>
    </div>
  );
}

export function FieldReviews(
  props: FieldPanelProps &
    CaptureProps & {
      focusId?: string | undefined;
      focusRequest?: number;
      suppressFocus?: boolean;
      onRunChecks?: (prId: string) => void;
      onNavigate?: (destination: FieldDestination) => void;
    },
) {
  const { snapshot, busy, onAction } = props;
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState('');
  const [merge, setMerge] = useState<{ id: string; title: string; revision: string } | null>(null);
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!props.focusId) return;
    if (props.focusId === 'new-submission') setCreating(true);
    setSearch('');
    // The guided tour scrolls each individual field into view itself.
    if (props.suppressFocus) return;
    const frame = requestAnimationFrame(() => {
      const element = Array.from(
        panel.current?.querySelectorAll<HTMLElement>('[data-object-id]') ?? [],
      ).find((element) => element.dataset.objectId === props.focusId);
      element?.focus({ preventScroll: true });
      element?.scrollIntoView?.({ block: 'nearest' });
    });
    return () => cancelAnimationFrame(frame);
  }, [props.focusId, props.focusRequest, props.suppressFocus]);
  const prs = snapshot.prs
    .filter((pr) =>
      `${pr.title} ${pr.description} ${pr.id} ${pr.revision}`
        .toLowerCase()
        .includes(search.trim().toLowerCase()),
    )
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [ticketId, setTicketId] = useState('');
  const [files, setFiles] = useState<SourceFile[]>([]);
  const [reviewerIds, setReviewerIds] = useState<FieldActorId[]>(defaultReviewers);
  return (
    <div ref={panel} className="field-object-panel h-full overflow-y-auto">
      <FieldPanelHeading
        title="Reviews"
        action={
          <Button size="sm" onClick={() => setCreating(!creating)} aria-expanded={creating}>
            <Plus className="size-4" aria-hidden="true" />
            New submission
          </Button>
        }
      >
        Submit a working increment. Your colleagues check it against the brief and agreed
        requirements, then send you detailed feedback.
      </FieldPanelHeading>
      <details className="border-line border-b px-5 py-3 text-sm">
        <summary className="cursor-pointer font-medium">How review works</summary>
        <ol className="text-fg-muted mt-3 list-decimal space-y-2 pl-5">
          <li>
            Write and test your code in the Workbench editor. Saving a workspace does not submit it.
          </li>
          <li>
            Submit from the editor or upload your complete project. Jordan reviews the product and
            Noor reviews the code by default. You can add other owners below.
          </li>
          <li>
            Checks and reviews start automatically. You can submit an early prototype with open
            questions; your reviewers separate defects from clarifications and new scope.
          </li>
          <li>
            Read the reports and colleague messages, agree the follow-up, and submit an updated
            version. Merge and release when the required evidence and approvals are ready.
          </li>
        </ol>
      </details>
      <form
        hidden={!creating}
        data-object-id="new-submission"
        tabIndex={-1}
        className="field-object-form space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          void onAction({
            type: 'pr.open',
            title,
            description,
            ticketIds: ticketId ? [ticketId] : [],
            files,
            reviewerIds,
          }).then((sent) => {
            if (sent) {
              setCreating(false);
              setTitle('');
              setDescription('');
              setFiles([]);
              setTicketId('');
            }
          });
        }}
      >
        <div data-field-tour="submission-title">
          <label className={FIELD_LABEL} htmlFor="pr-title">
            Title
          </label>
          <input
            id="pr-title"
            className={FIELD_INPUT}
            maxLength={200}
            required
            disabled={busy}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
        </div>
        <div data-field-tour="submission-context">
          <label className={FIELD_LABEL} htmlFor="pr-description">
            What changed, why, and how did you check it?
          </label>
          <textarea
            id="pr-description"
            className={FIELD_INPUT}
            rows={3}
            maxLength={12000}
            disabled={busy}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </div>
        <div data-field-tour="submission-ticket">
          <label className={FIELD_LABEL} htmlFor="pr-ticket">
            Linked ticket
          </label>
          <SelectField
            id="pr-ticket"
            disabled={busy}
            value={ticketId}
            onValueChange={(nextValue) => setTicketId(nextValue)}
            options={[
              { value: '', label: 'No ticket selected' },
              ...(snapshot.tickets.map((ticket) => ({
                value: ticket.id,
                label: <>{ticket.title}</>,
              })) ?? []),
            ]}
          />
        </div>
        <ReviewerPicker
          snapshot={snapshot}
          selected={reviewerIds}
          onChange={setReviewerIds}
          disabled={busy}
        />
        <NativeCapture
          {...props}
          input={{ title, description, ticketIds: ticketId ? [ticketId] : [], reviewerIds }}
          onComplete={() => {
            setCreating(false);
            setTitle('');
            setDescription('');
            setFiles([]);
            setTicketId('');
          }}
        />
        <SourcePicker onFiles={setFiles} files={files} busy={busy} />
        <p className="text-fg-muted text-xs">
          Your selected reviewers receive this exact version and the relevant project context. Their
          feedback appears here and in Conversations.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            type="submit"
            data-field-tour="submission-submit"
            disabled={busy || !files.length || !title.trim() || !reviewerIds.length}
          >
            <GitPullRequest className="size-4" aria-hidden="true" />
            Submit uploaded source
          </Button>
          <Button type="button" variant="ghost" onClick={() => setCreating(false)}>
            Close
          </Button>
        </div>
      </form>
      <div className="field-object-toolbar" data-field-tour="review-history">
        <div className="field-object-search">
          <label htmlFor="pr-search" className={FIELD_LABEL}>
            Find a submission
          </label>
          <input
            id="pr-search"
            type="search"
            className={FIELD_INPUT}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search title or revision"
          />
        </div>
      </div>
      {!snapshot.prs.length ? (
        <FieldEmpty title="Get feedback on your first increment">
          Build a small working change or an early prototype. Submit it from the Workbench IDE or
          upload the complete project to hear from your reviewers.
        </FieldEmpty>
      ) : !prs.length ? (
        <FieldEmpty title="No submissions match your search">
          Try another title or revision.
        </FieldEmpty>
      ) : (
        <div className="field-object-collection">
          {prs.map((pr) => {
            const suites = (['contracts', 'security', 'evaluation'] as const).map((suite) => ({
              suite,
              run: snapshot.runs
                .filter(
                  (run) =>
                    run.prId === pr.id && run.revision === pr.revision && run.suite === suite,
                )
                .at(-1),
            }));
            const passed = suites.filter(
              ({ run }) => run?.status === 'passed' && run.trusted,
            ).length;
            const currentReviews = snapshot.actors.flatMap((actor) => {
              const review = pr.reviews
                .filter((review) => review.actorId === actor.id && review.revision === pr.revision)
                .at(-1);
              return review ? [review] : [];
            });
            const approved = currentReviews.filter((review) => review.status === 'approved').length;
            const reviewSummary = currentReviews.some(
              (review) => review.status === 'changes_requested',
            )
              ? 'Changes requested'
              : currentReviews.some((review) => review.status === 'failed')
                ? 'Review needs attention'
                : currentReviews.some((review) => review.status === 'pending')
                  ? 'Review pending'
                  : approved
                    ? `${approved} colleague ${approved === 1 ? 'approval' : 'approvals'}`
                    : currentReviews.length
                      ? 'Feedback recorded'
                      : 'Review not requested';
            return (
              <article
                key={pr.id}
                tabIndex={-1}
                data-object-id={pr.id}
                data-focused={props.focusId === pr.id}
                className="field-object-card"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="text-lg font-semibold">{pr.title}</h3>
                    <div className="field-object-meta mt-2">
                      <span title={pr.revision}>
                        Revision <code>{pr.revision.slice(0, 8)}</code>
                      </span>
                      <span>{pr.fileCount} files</span>
                      <FieldTime value={pr.updatedAt} />
                    </div>
                  </div>
                  <FieldStatus value={pr.status} />
                </div>
                <p className="text-fg-muted mt-3 whitespace-pre-wrap text-sm leading-6">
                  {pr.description}
                </p>
                {pr.ticketIds.length ? (
                  <p className="text-fg-muted mt-3 text-sm">
                    Linked work:{' '}
                    {pr.ticketIds
                      .map((id) => snapshot.tickets.find((ticket) => ticket.id === id)?.title ?? id)
                      .join(' · ')}
                  </p>
                ) : null}
                <div className="field-review-lifecycle" aria-label="Revision lifecycle">
                  <div className="field-review-stage">
                    <span>1. Source</span>
                    <strong>Revision saved</strong>
                    <p>
                      {pr.provenance === 'workspace_capture'
                        ? 'Workspace capture'
                        : 'Learner-uploaded source'}
                    </p>
                  </div>
                  <div className="field-review-stage">
                    <span>2. Verification</span>
                    <strong>{passed} of 3 required suites passed</strong>
                    <p>Trusted results for this revision</p>
                  </div>
                  <div className="field-review-stage">
                    <span>3. Review</span>
                    <strong>{reviewSummary}</strong>
                    <p>Product feedback and technical approval</p>
                  </div>
                </div>
                <details className="text-sm">
                  <summary className="cursor-pointer">Required check results</summary>
                  <ul className="divide-line divide-y">
                    {suites.map(({ suite, run }) => (
                      <li
                        key={suite}
                        className="flex flex-wrap items-center justify-between gap-2 py-2"
                      >
                        <span>{runSuiteLabels[suite]}</span>
                        <span className="text-fg-muted">
                          {run?.status === 'passed' && run.trusted
                            ? 'Passed · trusted'
                            : run?.status === 'failed' && run.trusted
                              ? 'Failed · trusted'
                              : run?.status === 'pending'
                                ? 'Waiting to run'
                                : run?.status === 'running'
                                  ? 'Running'
                                  : 'No trusted passing result'}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-fg-muted py-2 text-xs">
                    Run these suites from Runs & release. Results from earlier revisions do not
                    count.
                  </p>
                </details>
                {props.onRunChecks ? (
                  <Button size="sm" variant="secondary" onClick={() => props.onRunChecks?.(pr.id)}>
                    View & run required checks
                  </Button>
                ) : null}
                {pr.reviews.length ? (
                  <section className="mt-5" aria-label="Colleague reviews">
                    <h4 className="text-sm font-semibold">Colleague reviews</h4>
                    <p className="text-fg-muted mt-1 text-sm">
                      “Reviewed” means feedback was recorded. Only “Approved” grants approval for
                      that revision.
                    </p>
                    <div className="divide-line mt-3 divide-y">
                      {[...pr.reviews].reverse().map((review) => (
                        <FieldReviewReportCard key={review.id} {...props} pr={pr} review={review} />
                      ))}
                    </div>
                  </section>
                ) : null}
                <ReviewRequest {...props} pr={pr} />
                <FieldSharing {...props} pr={pr} />
                {pr.status === 'open' ? (
                  <div className="border-line mt-5 border-t pt-4">
                    <p className="text-fg-muted mb-3 text-sm leading-6">
                      Merge requires all owner approvals and trusted contract, security, and
                      evaluation checks for this exact revision. The merge action verifies every
                      requirement.
                    </p>
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={busy}
                      onClick={() =>
                        setMerge({ id: pr.id, title: pr.title, revision: pr.revision })
                      }
                    >
                      Review & merge
                    </Button>
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      )}
      <ConfirmationDialog
        open={merge !== null}
        title="Merge this revision?"
        confirmLabel="Verify requirements & merge"
        cancelLabel="Keep reviewing"
        busy={busy}
        confirmDisabled={
          snapshot.prs.find((pr) => pr.id === merge?.id)?.revision !== merge?.revision
        }
        onCancel={() => setMerge(null)}
        onConfirm={() => {
          if (merge)
            void onAction({ type: 'pr.merge', prId: merge.id, revision: merge.revision }).then(
              () => {
                // FieldPage reports failed commands in its alert. Close the modal so the
                // failure is visible and accessible instead of hidden behind its backdrop.
                setMerge(null);
              },
            );
        }}
      >
        <p>{merge?.title}</p>
        <p className="mt-2">
          Revision <code title={merge?.revision}>{merge?.revision.slice(0, 8)}</code> will be merged
          only if its required approvals and trusted checks are valid.
        </p>
      </ConfirmationDialog>
    </div>
  );
}
